import {
  DeploymentSchemeRequest,
  DeploymentSchemeResults,
  DeploymentSchemeSearchArgs,
  DeploymentSchemeSimple,
  DeploymentSchemeWithGoals,
} from "types/deployment-schemes/deployment-scheme-types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DeploymentSchemesAPI } from "api";
import { sortDeploymentSchemeGoalsBySegmentName } from "utils/deploymentScheme/deploymentSchemeUtils";

const queryKeys = {
  all: () => ["deployment-schemes"] as const,
  searchBase: () => [...queryKeys.all(), "search"] as const,
  search: ({ query = "", size = 1000 }: DeploymentSchemeSearchArgs) =>
    [...queryKeys.searchBase(), { query, size }] as const,
  listAll: () => [...queryKeys.all(), "list-all"] as const,
  deploymentScheme: (id: number) => [...queryKeys.all(), id] as const,
};

/**
 * Fetches a list of DeploymentSchemeSimple instances - typically used within a select search.
 */
export const useAllDeploymentSchemes = <T = DeploymentSchemeSimple[]>(
  options: UseQueryOptions<DeploymentSchemeSimple[], unknown, T> = {},
) => useQuery<DeploymentSchemeSimple[], unknown, T>(queryKeys.listAll(), () => DeploymentSchemesAPI.getAll(), options);

/**
 * Fetches a paginated list of DeploymentScheme instances.
 */
export const useDeploymentSchemeSearch = <T = DeploymentSchemeResults>(
  args: DeploymentSchemeSearchArgs,
  options: UseQueryOptions<DeploymentSchemeResults, unknown, T> = {},
) =>
  useQuery<DeploymentSchemeResults, unknown, T>(queryKeys.search(args), () => DeploymentSchemesAPI.list(args), options);

/**
 * Fetches a DeploymentScheme by ID.
 *
 * NOTE: Goals are always sorted by segment name (see `sortDeploymentSchemeGoalsBySegmentName`).
 * The sort is applied via `select` so it runs whenever the cached data changes (fetch, refetch, or `setQueryData`).
 * A caller-provided `select` receives the already sorted data.
 */
export const useDeploymentScheme = <T extends DeploymentSchemeWithGoals>(
  id: number,
  options: UseQueryOptions<DeploymentSchemeWithGoals, unknown, T> = {},
) =>
  useQuery<DeploymentSchemeWithGoals, unknown, T>(queryKeys.deploymentScheme(id), () => DeploymentSchemesAPI.get(id), {
    ...options,
    select: (data) => {
      const sortedData = sortDeploymentSchemeGoalsBySegmentName(data);
      return options.select ? options.select(sortedData) : (sortedData as T);
    },
  });

/**
 * Mutation to create a new DeploymentScheme.
 */
export const useDeploymentSchemeCreate = (
  options: UseMutationOptions<DeploymentSchemeWithGoals, unknown, DeploymentSchemeRequest> = {},
) => {
  return useMutation((data) => DeploymentSchemesAPI.create(data), options);
};

/**
 * Mutation to update a DeploymentScheme.
 *
 * NOTE: This will automatically update the deployment scheme in the cache on success.
 */
export const useDeploymentSchemeUpdate = (
  id: number,
  options: UseMutationOptions<DeploymentSchemeWithGoals, unknown, DeploymentSchemeRequest> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data) => {
      return DeploymentSchemesAPI.update(id, data);
    },
    {
      ...options,
      onSuccess(data, variables, context) {
        queryClient.setQueryData(queryKeys.deploymentScheme(id), data);
        return options.onSuccess?.(data, variables, context);
      },
    },
  );
};

/**
 * Mutation to delete a DeploymentScheme.
 */
export const useDeploymentSchemeDelete = (options: UseMutationOptions<void, unknown, number> = {}) => {
  return useMutation((id) => DeploymentSchemesAPI.delete(id), options);
};
